import { fmtNum, fmtPrice } from "@/lib/api";
import { ClassChangeTip, HoverTip, marginText, weightChangeText } from "@/components/race/ClassChangeTip";

const FACTORS = [
  ["ls_sp", "Last start SP"], ["record", "Win record"], ["barrier", "Barrier"], ["class_change", "Class change"],
  ["weight_change", "Weight change (kg)"], ["ls_margin", "Last start margin"], ["track_record", "Track record"],
  ["dist_record", "Distance record"], ["first_up", "1st Up"], ["second_up", "2nd Up"], ["slow_track", "Slow track (places)"], ["heavy_track", "Heavy track (places)"],
];

const LastStart = ({ ls, today }) => {
  if (!ls) return <div className="text-xs text-slate-500">No last start data</div>;
  const cs = ls.class_score || {};
  return (
    <div className="text-xs text-slate-400 leading-relaxed" data-testid="breakdown-last-start">
      <span className="eyebrow mr-2">Last start</span>
      {[ls.venue, ls.date, ls.distance && `${fmtNum(ls.distance, 0)}m`, ls.track, ls.class_raw].filter(Boolean).join(" • ")}
      {" • "}{ls.weight != null ? `${fmtNum(ls.weight, 1)}kg` : "—"} • {ls.finish ? `${ls.finish}${ls.field_size ? `/${ls.field_size}` : ""}` : "—"}
      {" • "}{ls.margin != null ? `${fmtNum(ls.margin, 2)}L` : "—"} • SP {ls.sp != null ? `$${ls.sp}` : "—"}
      <span className="block">Class score: last start <span className="num text-cyan-300">{cs.score ?? "—"}</span>{cs.table ? ` (${cs.table})` : ""} → today <span className="num text-cyan-300">{today ?? "—"}</span></span>
    </div>
  );
};

export const HorseBreakdown = ({ r, testId }) => (
  <div className="bg-[#0B1120] border border-slate-800 rounded p-3 sm:p-4 space-y-3" data-testid={testId}>
    <LastStart ls={r.raw?.last_start} today={r.raw?.race_class_score} />
    <table className="w-full text-xs sm:text-sm">
      <thead>
        <tr className="eyebrow border-b border-slate-800 text-left">
          <th className="py-1.5 pr-2 font-normal">Factor</th><th className="py-1.5 px-2 font-normal text-right">Data</th>
          <th className="py-1.5 px-2 font-normal text-right hidden sm:table-cell">Band</th><th className="py-1.5 pl-2 font-normal text-right">Points</th>
        </tr>
      </thead>
      <tbody>
        {FACTORS.filter(([k]) => r.scores?.[k]).map(([k, label]) => {
          const s = r.scores[k];
          return (
            <tr key={k} className="border-b border-slate-800/50" data-testid={`${testId}-${k}`}>
              <td className="py-1.5 pr-2 text-slate-300">{label}</td>
              <td className="py-1.5 px-2 num text-right text-cyan-300">{s.value == null ? <span className="text-slate-600">n/a</span> : fmtNum(s.value, 2)}</td>
              <td className="py-1.5 px-2 num text-right text-slate-500 hidden sm:table-cell">{s.band || "—"}</td>
              <td className={`py-1.5 pl-2 num text-right font-semibold ${s.weighted > 0 ? "text-emerald-400" : s.weighted < 0 ? "text-rose-400" : "text-slate-500"}`}>
                {k === "class_change"
                  ? <ClassChangeTip today={r.raw?.race_class_score} ls={r.raw?.last_start} testId={`${testId}-class-tip`}>{s.weighted > 0 ? "+" : ""}{fmtNum(s.weighted, 2)}</ClassChangeTip>
                  : k === "ls_margin"
                  ? <HoverTip text={marginText(r.raw?.last_start, s)} testId={`${testId}-margin-tip`}>{s.weighted > 0 ? "+" : ""}{fmtNum(s.weighted, 2)}</HoverTip>
                  : k === "weight_change"
                    ? <HoverTip text={weightChangeText(r.raw?.weight, r.raw?.last_start && { ...r.raw.last_start, picnic: r.raw.ls_picnic, letter: r.raw.ls_letter_class, highweight: r.raw.ls_highweight })} testId={`${testId}-weight-tip`}>{s.weighted > 0 ? "+" : ""}{fmtNum(s.weighted, 2)}</HoverTip>
                    : <>{s.weighted > 0 ? "+" : ""}{fmtNum(s.weighted, 2)}</>}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
    <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs font-mono text-slate-400" data-testid={`${testId}-totals`}>
      {r.adjustment ? <span className="text-amber-300" data-testid={`${testId}-adjustment`}>Admin adj {r.adjustment > 0 ? "+" : ""}{r.adjustment}</span> : null}
      <span>Total <span className="text-white font-bold">{fmtNum(r.raw_exact ?? r.raw_score, 3)}</span></span>
      <span>Rating <span className="text-emerald-400 font-bold">{Number(r.rating).toFixed(1)}</span></span>
      <span>Chance <span className="text-white">{fmtNum(r.pct, 2)}%</span></span>
      <span>Price <span className="text-amber-400 font-bold">{fmtPrice(r.price)}</span></span>
    </div>
  </div>
);
